<template>
  <div>
    <b-row class="pt-2 pb-1">
      <b-col cols="6" class="d-flex align-items-center">
        <div class="d-flex align-items-center mr-auto ml-2">
          <button
            class="btn btn-sm rounded-circle p-25 cursor-pointer"
            :class="isDark ? 'btn-danger' : 'btn-primary'"
            :aria-label="$t('go_back')"
            @click="$router.push({ name: 'seproban_events' })"
          >
            <i class="feather icon-arrow-left arrow-icon"></i>
          </button>
          <div
            class="ml-1 text-capitalize text-dark cursor-pointer goback-text"
            @click="$router.push({ name: 'seproban_events' })"
          >
            {{ $t("go_back") }}
          </div>
        </div>
      </b-col>
      <b-col cols="6" class="pr-3 text-right">
        <button-filters
          @show-modal="$root.$emit('bv::show::modal', $event)"
          :filters-count="filtersCount"
          @clear-search-filters="clearSearchFilters"
        />
      </b-col>
    </b-row>

    <!-- Filters -->
    <filter-modal @action="buildFilters()" @clear="clearSearchFilters()">
      <template v-slot:main-filters>
        <filters
          :camera-filter.sync="cameraFilter"
          :camera-options="cameraOptions"
          :download-date-range.sync="downloadDateRange"
          :sent-date-range.sync="sentDateRange"
        />
      </template>
    </filter-modal>

    <b-card
      v-if="alarm.id || unit.description"
      class="mx-2 mb-0 seproban-event-details"
    >
      <h4 class="mb-1">{{ $t("seproban.image_details") }}</h4>
      <b-row>
        <b-col cols="12" sm="6" lg="4" class="mb-1">
          <small class="text-muted d-block">{{ $t("seproban.event_id") }}</small>
          <strong>#{{ idSeprobanAlarmEvent }}</strong>
        </b-col>
        <b-col cols="12" sm="6" lg="4" class="mb-1">
          <small class="text-muted d-block">{{ $t("seproban.alarm_id") }}</small>
          <strong>#{{ eventDetails.id_alarm || alarm.id }}</strong>
        </b-col>
        <b-col cols="12" sm="6" lg="4" class="mb-1">
          <small class="text-muted d-block">{{ $t("seproban.alarm_created_at") }}</small>
          <strong>
            {{
              eventDetails.alarm_created_at || alarm.created
                ? formatDateAssigned(eventDetails.alarm_created_at || alarm.created)
                : "-"
            }}
          </strong>
        </b-col>
        <b-col cols="12" sm="6" lg="4">
          <small class="text-muted d-block">{{ $t("seproban.unit") }}</small>
          <strong>{{ unit.description || "-" }}</strong>
        </b-col>
        <b-col cols="12" sm="6" lg="4">
          <small class="text-muted d-block">REMS</small>
          <strong>{{ unit.rems || "-" }}</strong>
        </b-col>
        <b-col cols="12" sm="6" lg="4">
          <small class="text-capitalize text-muted d-block">{{ $tc("region.title", 1) }}</small>
          <strong>{{ unit.region && unit.region.name ? unit.region.name : "-" }}</strong>
        </b-col>
        <b-col cols="12" sm="6" lg="4" class="mt-1">
          <small class="text-capitalize text-muted d-block">{{ $tc("municipality.title", 1) }}</small>
          <strong>{{ unit.municipality && unit.municipality.name ? unit.municipality.name : "-" }}</strong>
        </b-col>
        <b-col cols="12" sm="6" lg="4" class="mt-1">
          <small class="text-muted d-block">{{ $t("seproban.first_download_at") }}</small>
          <strong>{{ seprobanStats.firstDownloadedAt }}</strong>
        </b-col>
        <b-col cols="12" sm="6" lg="4" class="mt-1">
          <small class="text-muted d-block">{{ $t("seproban.last_download_at") }}</small>
          <strong>{{ seprobanStats.lastDownloadedAt }}</strong>
        </b-col>
        <b-col cols="12" sm="6" lg="4" class="mt-1">
          <small class="text-muted d-block">{{ $t("seproban.first_sent_at") }}</small>
          <strong>{{ seprobanStats.firstSentAt }}</strong>
        </b-col>
        <b-col cols="12" sm="6" lg="4" class="mt-1">
          <small class="text-muted d-block">{{ $t("seproban.last_sent_at") }}</small>
          <strong>{{ seprobanStats.lastSentAt }}</strong>
        </b-col>
        <b-col cols="12" sm="6" lg="4" class="mt-1">
          <small class="text-muted d-block">{{ $t("seproban.send_rate") }}</small>
          <strong>{{ seprobanStats.sendRate }}</strong>
        </b-col>
      </b-row>
    </b-card>

    <b-card class="m-2" no-body>
      <div class="m-2">
        <b-row>
          <b-col
            cols="12"
            md="6"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
          >
            <label class="text-capitalize">{{ $t("table.show") }}</label>
            <v-select
              v-model="perPage"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="perPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block mx-50"
            />
            <label class="text-capitalize">{{ $t("table.entries") }}</label>
          </b-col>
          <b-col cols="12" md="6">
            <div class="d-flex align-items-center justify-content-end">
              <b-button
                @click="downloadSnapshots()"
                variant="success"
                class="text-capitalize ml-1"
                type="button"
                :disabled="buttonExport"
              >
                <b-spinner v-if="buttonExport" small class="mr-50" />
                <feather-icon v-else icon="DownloadCloudIcon" size="15" />
                <span v-if="buttonExport" class="ml-25">
                  {{ $t("seproban.generating_images") }}
                </span>
              </b-button>
            </div>
          </b-col>
        </b-row>
      </div>
      <b-table
        ref="refListTable"
        class="position-relative"
        :items="fetchList"
        responsive
        :fields="tableColumns"
        primary-key="id"
        :sort-by.sync="sortBy"
        show-empty
        :empty-text="$t('no_record_found')"
        :sort-desc.sync="isSortDirDesc"
      >
        <template #head(images)="data">
          <span> {{ $tc(data.label, 1) }}</span>
        </template>
        <template #head(unit_name)="data">
          <span> {{ $tc(data.label, 2) }}</span>
        </template>
        <template #head(camera_name)="data">
          <span> {{ $tc(data.label, 2) }}</span>
        </template>
        <template #head(total_images)="data">
          <span> {{ $tc(data.label, 2) }}</span>
        </template>
        <template #head(created_at)="data">
          <span> {{ $tc(data.label, 2) }}</span>
        </template>
        <template #head(updated_at)="data">
          <span> {{ $tc(data.label, 2) }}</span>
        </template>
        <template #head(download)="data">
          <span>{{ $t(data.label) }}</span>
        </template>

        <template #cell(images)="data">
          <div class="hoverable">
            <b-img
              v-if="data.item.images.length > 0"
              :src="data.item.images[0].url"
              alt="path Image"
              style="max-height: 150px; width: fit-content; max-width: 500px"
              @click="openImageModal(data.item)"
              v-b-modal.image_modal
            ></b-img>
          </div>
        </template>
        <template #cell(download)="data">
          <b-button
            variant="outline-success"
            size="sm"
            :disabled="
              getCameraProxyId(data.item) == null ||
              buttonExport ||
              downloadingCameraId !== null
            "
            :title="$t('actions.download')"
            @click="downloadCameraSnapshots(data.item)"
          >
            <b-spinner
              v-if="downloadingCameraId === getCameraProxyId(data.item)"
              small
            />
            <feather-icon v-else icon="DownloadCloudIcon" size="15" />
          </b-button>
        </template>
      </b-table>
      <div class="mx-2 mb-2">
        <b-row>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-start"
          >
            <span class="text-muted text-capitalize">{{
              $t("table.showing", {
                from: dataMeta.from,
                to: dataMeta.to,
                of: dataMeta.of,
              })
            }}</span>
          </b-col>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-end"
          >
            <Pagination
              :totalList="totalList"
              :currentPage="currentPage"
              @updatePagination="update"
              :perPage="perPage"
            >
            </Pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>
    <image-modal />
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BTable,
  BButton,
  BDropdown,
  BDropdownItem,
  BPagination,
  BImg,
  BSpinner,
} from "bootstrap-vue";
import vSelect from "vue-select";
import { computed, ref } from "@vue/composition-api";
import useList from "./useList";
import { useToast } from "vue-toastification/composition";
import store from "@/store";
import { useUtils as useI18nUtils } from "@core/libs/i18n";
import Pagination from "@/components/Pagination.vue";
import ImageModal from "../../components/GridView/widgets/ImageModal.vue";
import Filters from "./Filters.vue";
import FilterModal from "@/components/Filters/FilterModal.vue";
import ButtonFilters from "@/components/Filters/ButtonFilters.vue";

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BDropdown,
    BDropdownItem,
    BPagination,
    BImg,
    BSpinner,
    vSelect,
    Pagination,
    ImageModal,
    Filters,
    FilterModal,
    ButtonFilters,
  },

  setup(_, context) {
    const isDark = computed(() => store.state.appConfig.layout.skin === "dark");
    //start pagination
    const update = (data) => {
      currentPage.value = data;
    };
    //end pagination

    const buttonExport = ref(false);
    const downloadingCameraId = ref(null);
    const { t } = useI18nUtils();
    const toast = useToast();
    const {
      fetchList,
      tableColumns,
      perPage,
      currentPage,
      totalList,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refListTable,
      refetchData,
      formatDateAssigned,
      buildFilters,
      filtersCount,
      cameraOptions,
      cameraFilter,
      downloadDateRange,
      sentDateRange,
      clearSearchFilters,
      downloadSnapshots: requestDownloadSnapshots,
      unit,
      alarm,
      idSeprobanAlarmEvent,
      imageRows,
      eventDetails,
    } = useList();

    const parseDate = value => {
      if (!value) return null;
      const date = new Date(typeof value === "string" ? value.replace(" ", "T") : value);
      return Number.isNaN(date.getTime()) ? null : date;
    };

    const formatStatDate = date => date ? formatDateAssigned(date) : "-";

    const seprobanStats = computed(() => {
      const rows = imageRows.value || [];
      const event = eventDetails.value || {};
      const images = rows.reduce(
        (result, row) => result.concat(Array.isArray(row.images) ? row.images : []),
        []
      );
      const downloadedDates = [
        event.first_snapshot_downloaded_at,
        event.last_snapshot_downloaded_at,
        event.downloaded_at,
        alarm.value.first_snapshot_downloaded_at,
        alarm.value.last_snapshot_downloaded_at,
        alarm.value.downloaded_at,
      ]
        .concat(rows.map(row => row.first_snapshot_downloaded_at || row.downloaded_at))
        .concat(images.map(image => image.downloaded_at || image.download_at))
        .map(parseDate)
        .filter(Boolean);
      const sentDates = [
        event.first_snapshot_sent_at,
        event.last_snapshot_sent_at,
        event.sent_seproban_at,
        alarm.value.first_snapshot_sent_at,
        alarm.value.last_snapshot_sent_at,
        alarm.value.sent_seproban_at,
        alarm.value.sent_at,
      ]
        .concat(rows.reduce((dates, row) => dates.concat([
          row.first_snapshot_sent_at,
          row.last_snapshot_sent_at,
          row.sent_seproban_at || row.sent_at,
        ]), []))
        .concat(images.map(image => image.sent_seproban_at || image.sent_at || image.uploaded_at))
        .map(parseDate)
        .filter(Boolean);
      const rowSentImages = rows.reduce(
        (total, row) => total + (Number(row.sent_images) || 0),
        0
      );
      const eventSentImages = Array.isArray(event.cameras) ? event.cameras.reduce(
        (total, camera) => total + (Number(camera.sent_images) || 0),
        0
      ) : 0;
      const sentImages = eventSentImages || Number(alarm.value.sent_images) || rowSentImages || images.filter(
        image => image.is_send === true || image.is_send === 1 || Boolean(image.sent_at || image.sent_seproban_at)
      ).length;
      const firstDownloadedDate = parseDate(event.first_snapshot_downloaded_at) ||
        (downloadedDates.length ? new Date(Math.min(...downloadedDates)) : null);
      const lastDownloadedDate = parseDate(event.last_snapshot_downloaded_at) ||
        (downloadedDates.length ? new Date(Math.max(...downloadedDates)) : null);
      const firstSentDate = parseDate(event.first_snapshot_sent_at) ||
        (sentDates.length ? new Date(Math.min(...sentDates)) : null);
      const lastSentDate = parseDate(event.last_snapshot_sent_at) ||
        (sentDates.length ? new Date(Math.max(...sentDates)) : null);
      const elapsedSeconds = firstSentDate && lastSentDate
        ? (lastSentDate.getTime() - firstSentDate.getTime()) / 1000
        : 0;
      const imagesPerSecond = sentImages > 0 && elapsedSeconds > 0
        ? sentImages / elapsedSeconds
        : null;

      return {
        firstDownloadedAt: formatStatDate(firstDownloadedDate),
        lastDownloadedAt: formatStatDate(lastDownloadedDate),
        firstSentAt: formatStatDate(firstSentDate),
        lastSentAt: formatStatDate(lastSentDate),
        sendRate: imagesPerSecond === null
          ? "-"
          : `${imagesPerSecond.toFixed(2)} ${t("seproban.images_per_second")}`,
      };
    });

    const downloadSnapshots = async () => {
      if (buttonExport.value || downloadingCameraId.value !== null) return;

      buttonExport.value = true;
      try {
        await requestDownloadSnapshots();
      } finally {
        buttonExport.value = false;
      }
    };

    const getCameraProxyId = item =>
      item.camera_proxy_id ??
      item.id_camera_proxy ??
      item.camera?.id_camera_proxy ??
      item.camera?.id ??
      null;

    const downloadCameraSnapshots = async item => {
      const cameraProxyId = getCameraProxyId(item);
      if (cameraProxyId == null || buttonExport.value || downloadingCameraId.value !== null) {
        return;
      }

      downloadingCameraId.value = cameraProxyId;
      try {
        await requestDownloadSnapshots(cameraProxyId);
      } finally {
        downloadingCameraId.value = null;
      }
    };

    const openImageModal = async (imagesInfo) => {
      const images = imagesInfo.images.map((image) => ({
        ...image,
        unit_name: imagesInfo.unit_name,
        camera_name: imagesInfo.camera_name,
        url: image.url,
        date: image.date,
      }));

      if (images.length) {
        await store.dispatch("image_modal/imageList", images);
      }
    };

    return {
      tableColumns,
      perPage,
      currentPage,
      totalList,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refListTable,
      buttonExport,
      downloadingCameraId,
      cameraOptions,
      cameraFilter,
      downloadDateRange,
      sentDateRange,
      filtersCount,

      unit,
      alarm,
      idSeprobanAlarmEvent,
      seprobanStats,
      eventDetails,
      isDark,

      update,
      fetchList,
      buildFilters,
      refetchData,
      formatDateAssigned,
      openImageModal,
      clearSearchFilters,
      downloadSnapshots,
      downloadCameraSnapshots,
      getCameraProxyId,
    };
  },
};
</script>

<style scoped>
.goback-text {
  font-size: 0.857rem;
}

.arrow-icon {
  font-size: 1rem;
}

.seproban-event-details {
  border-left: 4px solid var(--primary);
}
</style>
